import React, { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import {
  ArrowPathIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  CircleStackIcon,
  DocumentArrowUpIcon,
  GlobeAltIcon,
  InformationCircleIcon,
  PlayIcon,
} from '@heroicons/react/24/solid'
import ExcelParse from '../../../../middlewares/GlobalUtils/excelReader'
import generateTimestamp from '../../../../middlewares/GlobalUtils/generateTimeStamp'
import { useUser } from '../../../../store/userState'
import DownloadImportBtn from './DownloadImportBtn'
import { toast } from 'sonner'
const initialRequest = {
  siteCategory: '',
  scraperId: '',
  brandName: '',
  importData: '',
}

const StepLabel = ({ number, title, complete }) => (
  <div className="flex items-center gap-3">
    <span
      className={`flex h-7 w-7 items-center justify-center rounded-full border font-mono text-xs ${
        complete
          ? 'border-success/50 bg-success/15 text-success'
          : 'border-rule bg-surface text-muted'
      }`}
    >
      {complete ? <CheckCircleIcon className="h-4 w-4" /> : number}
    </span>
    <h3 className="font-display text-xl font-semibold tracking-wide text-paper">{title}</h3>
  </div>
)

const Workspace = () => {
  const { user } = useUser()
  const [brandScraperData, setBrandScraperData] = useState([])
  const [requestData, setRequestData] = useState(initialRequest)
  const [pasteInput, setPasteInput] = useState('')
  const [selectedFileName, setSelectedFileName] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const fileInputRef = useRef(null)

  const filteredBrandScrapers = useMemo(
    () =>
      [...brandScraperData]
        .filter(
          (item) =>
            item.category === requestData.siteCategory && item.status === 'Active'
        )
        .sort((a, b) => a.scraperId.localeCompare(b.scraperId)),
    [brandScraperData, requestData.siteCategory]
  )

  const importCount = Array.isArray(requestData.importData)
    ? requestData.importData.length
    : 0
  const isReady = Boolean(
    requestData.siteCategory &&
      requestData.scraperId &&
      importCount > 0
  )

  useEffect(() => {
    setIsLoading(true)
    axios
      .get('/api/webscrapers/all/scrapers')
      .then((response) => {
        if (response.data.message) {
          toast.error('Scraper inventory could not be loaded.')
        } else {
          setBrandScraperData(response.data.scrapers)
        }
      })
      .catch((error) => {
        console.error('Error:', error)
        toast.error('Scraper inventory could not be loaded.')
      })
      .finally(() => setIsLoading(false))
  }, [])

  const updateRequestData = (fields) => {
    setRequestData((previous) => ({ ...previous, ...fields }))
  }

  const selectCategory = (siteCategory) => {
    updateRequestData({
      siteCategory,
      scraperId: '',
      brandName: '',
    })
  }

  const clearDataSelection = () => {
    setRequestData(initialRequest)
    setPasteInput('')
    setSelectedFileName('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = async (readEvent) => {
      try {
        const parsedRows = await ExcelParse(readEvent.target.result)
        // ExcelParse returns one object per row (e.g. { 'Keyword/Style Number': 61301, '': '' }).
        // The backend expects a flat array of strings, matching the paste input path,
        // so take the first non-empty cell value from each row.
        const importData = parsedRows
          .map((row) => {
            const firstValue = Object.values(row).find(
              (value) => value !== null && value !== undefined && String(value).trim() !== ''
            )
            return firstValue !== undefined ? String(firstValue).trim() : ''
          })
          .filter(Boolean)

        if (importData.length === 0) {
          toast.error('No usable rows were found in this file.')
          return
        }

        updateRequestData({ importData })
        setPasteInput('')
        setSelectedFileName(file.name)
      } catch (error) {
        console.error('Error:', error)
        toast.error('This file could not be read. Use the provided template.')
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const handleTextareaChange = (event) => {
    const input = event.target.value
    const formattedArray = input
      .split(/[\n,]+/)
      .map((item) => item.trim())
      .filter(Boolean)

    setPasteInput(input)
    setSelectedFileName('')
    updateRequestData({ importData: formattedArray })
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleSubmitRequest = () => {
    if (!isReady || isSubmitting) return

    setIsSubmitting(true)
    const requestImport = {
      batchId: generateTimestamp(),
      siteCategory: requestData.siteCategory,
      scraperId: requestData.scraperId,
      brandName: requestData.brandName,
      importData: requestData.importData,
      addedBy: user.email,
    }

    axios
      .post('/api/scraper-requests/add', requestImport)
      .then((response) => {
        if (response.data.message) {
          toast.success(response.data.message)
          clearDataSelection()
          axios.post('/api/scraper-watcher/start-watching').catch((watcherError) => {
            console.error('Error starting watcher:', watcherError)
            toast.error(
              watcherError.response?.data?.message || 'The scraper watcher did not start.'
            )
          })
        } else {
          toast.error('The request could not be created.')
        }
      })
      .catch((error) => {
        console.error('Error:', error)
        toast.error(error.response?.data?.message || 'The request could not be created.')
      })
      .finally(() => setIsSubmitting(false))
  }

  return (
    <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_18rem]">
      <div className="space-y-4">
        <section className="nx-panel-soft p-4 sm:p-5">
          <StepLabel
            number="1"
            title="Choose site type"
            complete={Boolean(requestData.siteCategory)}
          />
          <p className="ml-10 mt-1 text-sm text-muted">
            Select where the scraper signs in and collects product data.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              {
                value: 'public',
                title: 'Public site',
                description: 'Collect data from a public storefront.',
                icon: GlobeAltIcon,
                tone: 'amber',
              },
              {
                value: 'b2b',
                title: 'B2B site',
                description: 'Use a business or dealer portal scraper.',
                icon: BuildingOffice2Icon,
                tone: 'signal',
              },
            ].map(({ value, title, description, icon: Icon, tone }) => {
              const selected = requestData.siteCategory === value
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => selectCategory(value)}
                  className={`flex items-start gap-3 rounded-xl border p-4 text-left transition ${
                    selected
                      ? 'border-signal bg-signal/10 shadow-[inset_3px_0_0_#42CBE5]'
                      : 'border-rule bg-surface hover:border-signal/40'
                  }`}
                  aria-pressed={selected}
                >
                  <Icon
                    className={`mt-0.5 h-5 w-5 shrink-0 ${
                      tone === 'amber' ? 'text-amber' : 'text-signal'
                    }`}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-paper">{title}</span>
                    <span className="mt-1 block text-xs leading-5 text-muted">{description}</span>
                  </span>
                  {selected && <CheckCircleIcon className="ml-auto h-5 w-5 shrink-0 text-success" />}
                </button>
              )
            })}
          </div>
        </section>

        <section className="nx-panel-soft p-4 sm:p-5">
          <StepLabel
            number="2"
            title="Choose scraper"
            complete={Boolean(requestData.scraperId)}
          />
          <p className="ml-10 mt-1 text-sm text-muted">
            Only active scrapers for the selected site type are shown.
          </p>

          {!requestData.siteCategory ? (
            <div className="mt-4 rounded-lg border border-dashed border-rule px-4 py-8 text-center text-sm text-muted">
              Choose a site type to see available scrapers.
            </div>
          ) : isLoading ? (
            <div className="mt-4 flex items-center justify-center gap-2 py-8 text-sm text-muted">
              <ArrowPathIcon className="h-4 w-4 animate-spin text-signal" />
              Loading scraper inventory
            </div>
          ) : filteredBrandScrapers.length === 0 ? (
            <div className="mt-4 rounded-lg border border-dashed border-rule px-4 py-8 text-center text-sm text-muted">
              No active scrapers are available for this site type.
            </div>
          ) : (
            <div className="mt-4 grid max-h-64 gap-2 overflow-y-auto pr-1 sm:grid-cols-2 lg:grid-cols-3">
              {filteredBrandScrapers.map((item) => {
                const brandName = item.name.split('-')[0]
                const selected = requestData.scraperId === item.scraperId
                return (
                  <button
                    key={item.scraperId}
                    type="button"
                    onClick={() =>
                      updateRequestData({ scraperId: item.scraperId, brandName })
                    }
                    className={`rounded-lg border px-3 py-3 text-left transition ${
                      selected
                        ? 'border-signal bg-signal/10'
                        : 'border-rule bg-surface hover:border-signal/40'
                    }`}
                    aria-pressed={selected}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-semibold text-paper">
                        {brandName}
                      </span>
                      {selected && <CheckCircleIcon className="h-4 w-4 shrink-0 text-success" />}
                    </span>
                    <span className="mt-1 block truncate font-mono text-[10px] uppercase tracking-wide text-muted">
                      {item.scraperId}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </section>

        <section className="nx-panel-soft p-4 sm:p-5">
          <StepLabel number="3" title="Add input data" complete={importCount > 0} />
          <div className="ml-10 mt-1 flex items-center gap-1.5 text-sm text-muted">
            Add style numbers, names, or keywords—one per line or separated by commas.
            <InformationCircleIcon className="h-4 w-4 shrink-0 text-signal" />
          </div>

          <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_15rem]">
            <div>
              <label htmlFor="scraper-input" className="nx-label">
                Paste values
              </label>
              <textarea
                id="scraper-input"
                className="nx-input min-h-36 resize-y leading-6"
                placeholder={'10021\nTrail runner\nwaterproof boots'}
                value={pasteInput}
                onChange={handleTextareaChange}
              />
            </div>

            <div className="flex min-h-36 flex-col items-center justify-center rounded-xl border border-dashed border-rule bg-surface p-4 text-center">
              <DocumentArrowUpIcon className="h-6 w-6 text-signal" />
              <p className="mt-2 max-w-full truncate text-sm font-semibold text-paper">
                {selectedFileName || 'Import a spreadsheet'}
              </p>
              <p className="mt-1 text-xs text-muted">ODS or supported workbook</p>
              <label className="nx-btn-secondary mt-3 cursor-pointer text-xs">
                Choose file
                <input
                  type="file"
                  hidden
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
              </label>
              <div className="mt-2 text-xs">
                <DownloadImportBtn />
              </div>
            </div>
          </div>
        </section>
      </div>

      <aside className="nx-panel sticky top-24 p-5">
        <div className="flex items-center justify-between gap-3 border-b border-rule pb-4">
          <div>
            <p className="nx-manifest-kicker">Run manifest</p>
            <h3 className="mt-1 font-display text-2xl tracking-wide text-paper">
              Scrape request
            </h3>
          </div>
          <CircleStackIcon className="h-6 w-6 text-signal" />
        </div>

        <dl className="space-y-4 py-5">
          <div>
            <dt className="font-mono text-[10px] uppercase tracking-wider text-muted">
              Site type
            </dt>
            <dd className="mt-1 text-sm text-paper">
              {requestData.siteCategory
                ? requestData.siteCategory === 'b2b'
                  ? 'B2B site'
                  : 'Public site'
                : 'Not selected'}
            </dd>
          </div>
          <div>
            <dt className="font-mono text-[10px] uppercase tracking-wider text-muted">
              Scraper
            </dt>
            <dd className="mt-1 truncate text-sm text-paper">
              {requestData.brandName || 'Not selected'}
            </dd>
          </div>
          <div>
            <dt className="font-mono text-[10px] uppercase tracking-wider text-muted">
              Input
            </dt>
            <dd className="mt-1 text-sm text-paper">
              {importCount > 0 ? `${importCount} item${importCount === 1 ? '' : 's'}` : 'No data'}
            </dd>
          </div>
        </dl>

        <div className={`mb-3 ${isReady ? 'nx-chip-ok' : 'nx-chip-warn'}`}>
          {isReady ? 'Ready to run' : 'Complete all steps'}
        </div>
        <button
          type="button"
          onClick={handleSubmitRequest}
          className="nx-btn-primary w-full"
          disabled={!isReady || isSubmitting}
        >
          {isSubmitting ? (
            <>

              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              Starting request
            </>
          ) : (
            <>

              <PlayIcon className="h-4 w-4" />
              Run scraper
            </>
          )}
        </button>
        <button
          type="button"
          onClick={clearDataSelection}
          className="nx-btn-ghost mt-2 w-full"
          disabled={
            !requestData.siteCategory &&
            !requestData.scraperId &&
            importCount === 0
          }
        >
          Reset request
        </button>
      </aside>
    </div>
  )
}

export default Workspace
